export const WEEKDAY_IDS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

/** Monday-start week containing referenceDate (defaults to today). */
export function scheduledDateForDayName(dayNameId: string, referenceDate = new Date()): string {
  const idx = WEEKDAY_IDS.indexOf(dayNameId.toLowerCase() as (typeof WEEKDAY_IDS)[number])
  if (idx < 0) return referenceDate.toISOString().slice(0, 10)

  const ref = new Date(referenceDate)
  ref.setHours(12, 0, 0, 0)
  const day = ref.getDay()
  const mondayOffset = day === 0 ? -6 : 1 - day

  const monday = new Date(ref)
  monday.setDate(ref.getDate() + mondayOffset)

  const target = new Date(monday)
  target.setDate(monday.getDate() + idx)
  return target.toISOString().slice(0, 10)
}

export function normalizeTimeHHMM(value: string): string {
  const trimmed = value.trim()
  return trimmed.length >= 5 ? trimmed.slice(0, 5) : trimmed
}

export function workoutDurationMinutes(startTime: string, endTime: string): number {
  const [startH, startM] = normalizeTimeHHMM(startTime).split(':').map(Number)
  const [endH, endM] = normalizeTimeHHMM(endTime).split(':').map(Number)
  const startTotal = startH * 60 + startM
  let endTotal = endH * 60 + endM
  if (endTotal <= startTotal) endTotal += 24 * 60
  return Math.max(30, endTotal - startTotal)
}

export function workoutEndTimeFromStart(startTime: string, durationMinutes: number): string {
  const [h, m] = normalizeTimeHHMM(startTime).split(':').map(Number)
  const total = h * 60 + m + durationMinutes
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

export function preferredWorkoutStart(profile: Record<string, unknown>): string | null {
  const raw = profile.preferred_workout_start
  if (raw == null || String(raw).trim() === '') return null
  return normalizeTimeHHMM(String(raw))
}

export function normalizeScheduleForProfile(
  profile: Record<string, unknown>,
  schedule: Record<string, unknown>[],
): Record<string, unknown>[] {
  const restDays = new Set(
    ((profile.rest_days as string[] | undefined) ?? []).map((d) => d.toLowerCase()),
  )
  const preferredStart = preferredWorkoutStart(profile)

  return schedule.map((block) => {
    const dayId = String(block.day_name_id ?? '').toLowerCase()
    let next = block

    if (restDays.has(dayId)) {
      next = {
        ...block,
        window_type: 'recovery',
        block_title: block.block_title ?? 'Active Recovery',
        focus_subtitle: block.focus_subtitle ?? 'Scheduled rest day',
        exercises: [],
        coach_why_rationale:
          block.coach_why_rationale ??
          'Your chosen rest day — mobility, hydration, and sleep take priority over training load.',
      }
    }

    const exercises = (next.exercises as unknown[] | undefined) ?? []
    if (!preferredStart || restDays.has(dayId) || exercises.length === 0) return next

    const duration = workoutDurationMinutes(
      String(next.start_time ?? preferredStart),
      String(next.end_time ?? workoutEndTimeFromStart(preferredStart, 60)),
    )

    return {
      ...next,
      start_time: preferredStart,
      end_time: workoutEndTimeFromStart(preferredStart, duration),
    }
  })
}
